"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Target, PoundSterling, Zap, TrendingUp, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const benefits = [{
  id: 1,
  title: "Strategic ownership",
  badge: "Direct accountability",
  icon: Target,
  summary: "I embed directly with your team and take full responsibility for your organic search trajectory.",
  description: "Unlike agency account managers juggling dozens of disparate accounts, I focus deeply on your business model, customer journey, and strategic search priorities."
}, {
  id: 2,
  title: "Cost-effective senior expertise",
  badge: "50%–70% cost saving",
  icon: PoundSterling,
  summary: "Gain Director-level search leadership at a fraction of full-time employment overheads.",
  description: "A permanent SEO Director in the UK costs £80,000–£120,000 annually before recruitment fees, benefits, and national insurance. My fractional model delivers immediate executive leadership without long-term liabilities."
}, {
  id: 3,
  title: "Faster implementation",
  badge: "Rapid execution",
  icon: Zap,
  summary: "Quick onboarding with zero agency bureaucracy or multi-week approval friction.",
  description: "I work straight into your workflows and CMS. Targeted technical improvements alone—without waiting months for new links—have generated a 128% lift in organic clicks within 90 days for UK service businesses."
}, {
  id: 4,
  title: "Sustainable in-house capability",
  badge: "Compounding value",
  icon: TrendingUp,
  summary: "Build permanent organic competence within your internal marketing and development teams.",
  description: "I establish structured documentation, clear content briefs, and standard operating procedures (SOPs) so your internal team keeps compounding search growth long after my direct engagement ends."
}, {
  id: 5,
  title: "Commercial revenue focus",
  badge: "Pipeline alignment",
  icon: BarChart3,
  summary: "Track qualified pipeline, booked calls, and paying customers rather than vanity traffic.",
  description: "Rankings mean nothing if they fail to attract high-intent buyers. Every campaign is engineered to capture prospective customers ready to purchase your core services."
}];
export default function WhyFractional() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        delay: shouldReduceMotion ? 0 : i * 0.08,
        ease: "easeOut"
      }
    })
  };
  return <section id="whyfractionalworks" data-nav="light" className="relative bg-background py-20 md:py-28 overflow-hidden">
      {/* Subtle decorative background glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-secondary/30 rounded-full blur-3xl pointer-events-none -z-10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider border-primary/20 bg-secondary/50 text-foreground">
            Senior Consultancy Model
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-5">
            Why my fractional SEO consultancy works
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            This fractional model delivers higher accountability, faster execution, and stronger commercial outcomes than bloated agency retainers or expensive permanent hires.
          </p>
        </div>

        {/* 5 Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {benefits.map((benefit, i) => {
          const IconComponent = benefit.icon;
          const isWideCard = i === 3 || i === 4;
          return <motion.div key={benefit.id} data-index={i} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.15
          }} variants={cardVariants} className={isWideCard && i === 3 ? "lg:col-span-1 md:col-span-1" : isWideCard && i === 4 ? "lg:col-span-2 md:col-span-2" : "col-span-1"}>
                <Card className="h-full border border-border/80 bg-card shadow-sm hover:shadow-md hover:border-primary/40 transition-[box-shadow,border-color] duration-200 flex flex-col justify-between">
                  <CardHeader className="space-y-4 pb-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="p-3 rounded-lg bg-primary/10 text-primary">
                        <IconComponent className="size-6 text-primary" />
                      </div>
                      <Badge variant="secondary" className="font-medium text-xs text-foreground bg-muted">
                        {benefit.badge}
                      </Badge>
                    </div>
                    
                    <CardTitle className="text-xl font-bold font-serif text-foreground pt-1">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-3 pt-0 flex-1 flex flex-col justify-between">
                    <p className="text-sm font-semibold text-foreground/90 leading-snug">
                      {benefit.summary}
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Closing summary & CTA Box */}
        <div className="rounded-2xl border border-primary/20 bg-muted/40 p-8 sm:p-10 lg:p-12 shadow-xs">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl text-left space-y-3">
              <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                <ShieldCheck className="size-5" />
                <span>Direct senior engagement</span>
              </div>
              <p className="text-lg sm:text-xl font-medium font-serif text-foreground leading-snug">
                Instead of paying an ongoing retainer to an agency delivering generic content and vanity metrics, get a dedicated fractional SEO lead who shapes your search direction and ensures SEO drives real business impact.
              </p>
              <div className="flex flex-wrap items-center gap-y-2 gap-x-6 pt-2 text-xs sm:text-sm text-muted-foreground font-sans">
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary" /> No long-term lock-in
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary" /> Direct one-to-one access
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary" /> Custom commercial roadmap
                </span>
              </div>
            </div>

            <div className="w-full lg:w-auto flex-shrink-0">
              <Button asChild size="lg" className="w-full sm:w-auto h-12 px-8 text-base font-medium rounded-lg shadow-sm">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}